declare function gtag(...args: unknown[]): void;

export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ?? 'G-X2TRPZWBFD';

/**
 * Configure GA4. The gtag.js script is loaded via a static <script> tag in
 * index.html so that browsers/CDNs never block it. This function sends the
 * required `gtag('js')` timestamp and config calls.
 */
export function initGA(): void {
  if (typeof gtag !== 'function') return;
  gtag('js', new Date());
  gtag('set', { app_id: 'agentica_chat' });
  gtag('config', GA_MEASUREMENT_ID, {
    page_path: '/chat',
    send_page_view: true,
  });
}

function track(eventName: string, params?: Record<string, unknown>): void {
  try {
    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
    }
  } catch {
    // Never allow analytics failures to affect product flows.
  }
}

/**
 * Set GA4 user_id for signed-in users (enables joining to Stripe in reports).
 * Call with null on logout so GA treats subsequent hits as anonymous.
 */
export function setGtagUserId(userId: string | null): void {
  if (typeof gtag !== 'function') return;
  if (userId != null && userId !== '') {
    gtag('set', { user_id: userId });
    gtag('config', GA_MEASUREMENT_ID, { user_id: userId });
  } else {
    gtag('set', { user_id: null });
    gtag('config', GA_MEASUREMENT_ID, { user_id: null });
  }
}

// ─── Chat events ───────────────────────────────────────────────────

export function trackMessageSent(params?: { session_id?: string; message_index?: number }): void {
  track('message_sent', params ?? {});
}

export function trackGenerationStopped(): void {
  track('generation_stopped');
}

export function trackNewChatStarted(): void {
  track('new_chat_started');
}

export function trackSessionSelected(sessionId: string): void {
  track('session_selected', { session_id: sessionId });
}

export function trackSessionDeleted(sessionId: string): void {
  track('session_deleted', { session_id: sessionId });
}

export function trackArtifactExported(format: string): void {
  track('artifact_exported', { export_format: format });
}

export function trackFileAttached(count: number): void {
  track('file_attached', { file_count: count });
}

export function trackAgentError(errorMessage: string): void {
  track('agent_error', { error_message: errorMessage.slice(0, 200) });
}

// ─── Agent management events ───────────────────────────────────────

export function trackAgentStopped(sessionId: string): void {
  track('agent_stopped', { session_id: sessionId });
}

export function trackAgentStarted(sessionId: string): void {
  track('agent_started', { session_id: sessionId });
}

export function trackAgentRenamed(sessionId: string): void {
  track('agent_renamed', { session_id: sessionId });
}

// ─── Settings events ───────────────────────────────────────────────

export function trackSettingsOpened(tab: 'general' | 'billing'): void {
  track('settings_opened', { tab });
}

export function trackSettingsTabChanged(tab: 'general' | 'billing'): void {
  track('settings_tab_changed', { tab });
}

export function trackActivityVisibilityChanged(visibility: string): void {
  track('activity_visibility_changed', { visibility });
}

export function trackSubagentCardsChanged(enabled: boolean): void {
  track('subagent_cards_changed', { enabled });
}

export function trackLogout(): void {
  track('logout');
}

// ─── Billing / e-commerce events ───────────────────────────────────

export function trackPlanUpgradeClicked(planId: string, price: number): void {
  track('begin_checkout', {
    currency: 'USD',
    value: price,
    items: [{ item_id: planId, item_name: planId, price }],
  });
}

export function trackPlanDowngradeClicked(planId: string, price: number): void {
  track('plan_downgrade_clicked', {
    currency: 'USD',
    value: price,
    plan_id: planId,
  });
}

export function trackPlanChangeConfirmed(planId: string, price: number): void {
  track('purchase', {
    currency: 'USD',
    value: price,
    transaction_id: `plan_change_${planId}_${Date.now()}`,
    items: [{ item_id: planId, item_name: planId, price }],
  });
}

export function trackSubscriptionCancelled(): void {
  track('subscription_cancelled');
}

export function trackSubscriptionResumed(): void {
  track('subscription_resumed');
}

export function trackManageSubscriptionClicked(): void {
  track('manage_subscription_clicked');
}

export function trackHelpEmailClicked(): void {
  track('help_email_clicked');
}

export function trackDemosClicked(): void {
  track('demos_clicked');
}

export function trackDemoLaunched(demoName: string): void {
  track('demo_launched', { demo_name: demoName });
}

// ─── Funnel intent / auth bridge events ────────────────────────────

export type SignInPromptSource =
  | 'ephemeral_banner'
  | 'anon_reset_modal'
  | 'insufficient_credits'
  | 'mobile_island';

export function trackSignInPromptShown(source: SignInPromptSource): void {
  track('sign_in_prompt_shown', { source });
}

export function trackSignInClicked(source: SignInPromptSource): void {
  track('sign_in_clicked', { source });
}
